import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Trophy, ChevronRight, TrendingDown, Zap, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

function Sparkline({ points = [], color = "#a855f7", max = 100 }) {
  if (!points.length) return null;
  const w = 88, h = 26, min = 0;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const xy = points.map((p, i) => {
    const x = i * step;
    const y = h - ((Math.max(min, Math.min(max, p)) - min) / (max - min)) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden>
      <polyline points={xy.join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.length > 0 && (() => {
        const last = xy[xy.length - 1].split(",");
        return <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />;
      })()}
    </svg>
  );
}

// Robot Report Card — shows the latest robot-playtest score per game + its trend, so creators can
// watch a game's playability climb as they apply fixes. Renders nothing until at least one run exists.
export default function RobotReportCard() {
  const [games, setGames] = useState(null);
  const [hasEligible, setHasEligible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/creator/botreports/recent").then((r) => {
      if (!alive) return;
      const list = r.data?.games || [];
      setGames(list);
      // No robot runs yet? Check if the creator has a 3D game worth inviting them to test.
      if (list.length === 0) {
        api.get("/games").then((g) => {
          if (!alive) return;
          const arr = Array.isArray(g.data) ? g.data : (g.data?.games || []);
          const BOT_2D = ["platformer", "runner", "shooter", "maze", "racing"];
          setHasEligible(arr.some((x) => ((x.config || {}).dimension) === "3d" || BOT_2D.includes((x.config || {}).template)));
        }).catch(() => {});
      }
    }).catch(() => setGames([]));
    return () => { alive = false; };
  }, []);

  if (!games) return null;

  const cls = (s) => (s >= 75 ? "text-emerald-400" : s >= 45 ? "text-amber-400" : "text-rose-400");
  const dot = (s) => (s >= 75 ? "#34d399" : s >= 45 ? "#fbbf24" : "#fb7185");
  const fpsDot = (f) => (f >= 50 ? "#34d399" : f >= 30 ? "#fbbf24" : "#fb7185");
  const fpsCls = (f) => (f >= 50 ? "text-emerald-400" : f >= 30 ? "text-amber-400" : "text-rose-400");

  // Empty-state: invite creators with a 3D game to try the robot playtester.
  if (games.length === 0) {
    if (!hasEligible) return null;
    return (
      <div className="mb-8 rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 p-5 flex items-center gap-4" data-testid="robot-report-empty">
        <div className="w-12 h-12 rounded-full grid place-items-center bg-violet-500/20 shrink-0"><Bot className="w-6 h-6 text-violet-400" /></div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-extrabold">Meet your Robot Playtester</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Let an AI robot play your 2D or 3D game, score it out of 100, and auto-fix problems. Games scoring 80+ earn a Certified Fun badge in the arcade.</p>
        </div>
        <button onClick={() => navigate("/app/games")} data-testid="robot-report-empty-cta"
          className="shrink-0 rounded-full px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90">
          Try it
        </button>
      </div>
    );
  }

  return (
    <div className="mb-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="robot-report-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full grid place-items-center bg-violet-500/15"><Bot className="w-5 h-5 text-violet-400" /></div>
        <div>
          <h3 className="font-display text-lg font-extrabold">Robot Report Card</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">How your games score when the AI robot plays them</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {games.map((g) => (
          <button key={g.game_id} onClick={() => navigate(`/app/build/${g.game_id}`)}
            data-testid={`robot-card-${g.game_id}`}
            className="group text-left rounded-xl border border-[hsl(var(--border))] p-3 hover:border-violet-500/50 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold truncate flex-1">{g.title}</span>
              {g.best_score > 0 && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 text-[9px] font-bold text-amber-400" data-testid={`robot-card-best-${g.game_id}`} title="Best score so far">
                  <Trophy className="w-2.5 h-2.5" /> {g.best_score}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-violet-400" />
            </div>
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className={`text-3xl font-black leading-none ${cls(g.latest_score)}`} data-testid={`robot-card-score-${g.game_id}`}>{g.latest_score}</div>
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mt-1">
                  {g.reached ? "🏆 completed" : `${g.findings} to fix`} · {g.runs} run{g.runs === 1 ? "" : "s"}
                </div>
              </div>
              {g.history.length >= 2
                ? <Sparkline points={g.history} color={dot(g.latest_score)} />
                : <span className="text-[10px] italic text-[hsl(var(--muted-foreground))]" data-testid={`robot-card-firstrun-${g.game_id}`}>first run</span>}
            </div>
            {/* FPS performance row — track frame-rate over time */}
            {g.latest_fps > 0 && (
              <div className="mt-2 flex items-end justify-between gap-2 pt-2 border-t border-[hsl(var(--border))]" data-testid={`robot-card-fps-${g.game_id}`}>
                <div className="flex items-center gap-1.5">
                  <Zap className={`w-3.5 h-3.5 ${fpsCls(g.latest_fps)}`} />
                  <span className={`text-sm font-bold ${fpsCls(g.latest_fps)}`} data-testid={`robot-card-fps-val-${g.game_id}`}>{g.latest_fps}</span>
                  <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">fps</span>
                  {g.latest_errors > 0 && (
                    <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 px-1.5 py-0.5 text-[9px] font-bold text-rose-400" data-testid={`robot-card-errors-${g.game_id}`} title="Runtime errors in the last run">
                      <AlertTriangle className="w-2.5 h-2.5" /> {g.latest_errors} err
                    </span>
                  )}
                </div>
                {g.fps_history && g.fps_history.filter((f) => f > 0).length >= 2
                  ? <Sparkline points={g.fps_history} color={fpsDot(g.latest_fps)} max={60} />
                  : null}
              </div>
            )}
            {g.dropped && (
              <div className="mt-2 flex items-center gap-1 rounded-lg bg-rose-500/10 border border-rose-500/30 px-2 py-1 text-[10px] font-semibold text-rose-400" data-testid={`robot-card-drop-${g.game_id}`}>
                <TrendingDown className="w-3 h-3" /> Dropped from {g.prev_score} — a recent edit may have hurt it. Re-run the robot.
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
